import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

import ApiKeyManager      from '../components/enterprise/ApiKeyManager';
import SandboxEnvironment from '../components/enterprise/SandboxEnvironment';
import RateLimitDashboard from '../components/enterprise/RateLimitDashboard';

const TABS = ['Overview','API Keys','Sandbox','Rate Limits','Support'];

export default function PartnerPortalPage() {
  const { user, logout } = useAuth();
  const { activeTenant, clearTenant } = useTenant();
  const navigate = useNavigate();
  const [tab, setTab]       = useState('Overview');
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!activeTenant) { navigate('/onboarding'); return; }
    api.get('/tenant/analytics/api-calls?days=30')
      .then(r => setAnalytics(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeTenant, navigate]);

  const S = {
    page:   { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' },
    header: { background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px', height: 56 },
    body:   { flex: 1, padding: 24 },
    tabs:   { display: 'flex', gap: 4, marginBottom: 24, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 4 },
    tab:    (a) => ({ padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none', background: a ? 'var(--accent)' : 'transparent', color: a ? '#fff' : 'var(--text-2)' }),
    card:   { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 },
    grid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 16, marginBottom: 24 },
    stat:   { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 },
  };

  const totalCalls   = analytics.reduce((a, d) => a + (d.total || 0), 0);
  const totalErrors  = analytics.reduce((a, d) => a + (d.errors || 0), 0);
  const avgRespTime  = analytics.length ? Math.round(analytics.reduce((a, d) => a + (d.avg_response_time || 0), 0) / analytics.length) : 0;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: 'linear-gradient(135deg,#89b4fa,#74c7ec)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🔑</div>
        <span style={{ fontWeight: 700, fontSize: 14 }}>Partner Portal</span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#89b4fa22', color: '#89b4fa', letterSpacing: 1 }}>API PARTNER</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{activeTenant?.name}</span>
        <button onClick={() => { logout(); clearTenant(); navigate('/login'); }} className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }}>Sign out</button>
      </div>

      <div style={S.body}>
        <div style={S.tabs}>
          {TABS.map(t => <button key={t} style={S.tab(tab === t)} onClick={() => setTab(t)}>{t}</button>)}
        </div>

        {tab === 'Overview' && !loading && (
          <>
            <div style={S.grid}>
              {[
                { label: 'API Calls (30d)',  val: totalCalls.toLocaleString(),  icon: '📡' },
                { label: 'Errors (30d)',     val: totalErrors.toLocaleString(), icon: '⚠️' },
                { label: 'Avg Response',     val: `${avgRespTime}ms`,           icon: '⚡' },
                { label: 'Success Rate',     val: totalCalls ? `${Math.round(((totalCalls - totalErrors) / totalCalls) * 100)}%` : 'N/A', icon: '✅' },
              ].map(m => (
                <div key={m.label} style={S.stat}>
                  <div style={{ fontSize: 20, marginBottom: 8 }}>{m.icon}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, marginBottom: 4 }}>{m.val}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{m.label}</div>
                </div>
              ))}
            </div>
            {analytics.length > 0 && (
              <div style={S.card}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Daily API Calls — Last 30 Days</h3>
                <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 80 }}>
                  {analytics.slice(-30).map((d, i) => {
                    const max = Math.max(...analytics.map(x => x.total || 0), 1);
                    const h = Math.round(((d.total || 0) / max) * 80);
                    return (
                      <div key={i} title={`${d.date}: ${d.total} calls`}
                        style={{ flex: 1, height: h, background: d.errors > 0 ? '#f38ba844' : '#5865f244', borderRadius: 3, minWidth: 4 }} />
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {tab === 'API Keys'    && <ApiKeyManager partnerView />}
        {tab === 'Sandbox'     && <SandboxEnvironment />}
        {tab === 'Rate Limits' && <RateLimitDashboard />}
        {tab === 'Support'     && (
          <div style={S.card}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Support Tickets</h3>
            <p style={{ fontSize: 13, color: 'var(--text-2)' }}>Need help with API integration? Submit a support ticket and our team will respond within 24 hours.</p>
            <button onClick={() => navigate('/')} className="btn btn-primary" style={{ marginTop: 16 }}>Open API Workspace → Create Ticket</button>
          </div>
        )}
      </div>
    </div>
  );
}
